import { useTranslation } from 'react-i18next'
import { Navigate, useParams } from 'react-router-dom'
import { MarketingPage } from '@/components/marketing/marketing-page'
import { ProseSection, ProseParagraphs } from '@/components/marketing/prose-section'
import { MarketingCta } from '@/components/marketing/marketing-cta'
import { RelatedLinks } from '@/components/marketing/related-links'
import { arrayKey } from '@/components/marketing/i18n-array'
import { JsonLd } from '@/components/seo/json-ld'
import { findBlogPost, type BlogPost } from '@/content/blog/posts'
import { buildBlogRelatedLinks } from '@/content/blog/blog-related'
import { blogPostBreadcrumb, blogPostingSchema } from '@/content/blog/blog-schema'

/** Renders one article from its registry entry; all prose comes from i18n. */
function BlogArticle({ post }: { readonly post: BlogPost }) {
  const { t, i18n } = useTranslation()
  const key = post.i18nKey
  const path = `/blog/${post.slug}`
  const heading = t(`blog.${key}.h1`)
  const description = t(`blog.${key}.seo.description`)
  const blogLabel = t('blog.index.crumb')

  return (
    <MarketingPage
      seoTitle={t(`blog.${key}.seo.title`)}
      seoDescription={description}
      path={path}
      eyebrow={t(`blog.${key}.eyebrow`)}
      heading={heading}
      lead={t(`blog.${key}.lead`)}
    >
      <JsonLd data={blogPostBreadcrumb(blogLabel, heading, post.slug)} />
      <JsonLd
        data={blogPostingSchema({
          post,
          headline: heading,
          description,
          language: i18n.language,
        })}
      />

      {post.sections.map((name) => (
        <ProseSection key={name} title={t(`blog.${key}.${name}.title`)}>
          <ProseParagraphs paragraphs={arrayKey(t, `blog.${key}.${name}.body`)} />
        </ProseSection>
      ))}

      <MarketingCta
        title={t(`blog.${key}.cta.title`)}
        body={t(`blog.${key}.cta.body`)}
        buttonLabel={t(`blog.${key}.cta.button`)}
        to="/"
      />
      <RelatedLinks
        heading={t('marketing.common.relatedHeading')}
        links={buildBlogRelatedLinks(t, post.slug, post.relatedKeys)}
      />
    </MarketingPage>
  )
}

/** /blog/:slug — resolves the post or redirects to /blog for an unknown slug. */
export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>()
  const post = slug ? findBlogPost(slug) : undefined
  if (!post) return <Navigate to="/blog" replace />
  return <BlogArticle post={post} />
}
